import { Tag } from '@wheely/ui-kit';
import { useMemo, useCallback, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { IDriverBrief } from '@/modules/driver/model';
import { IJourney } from '@/modules/journey/model';
import { errorHandling } from '@/services/errorHandling';
import { EntityPage } from '@/shared/models/entity-page';
import { getStatusIntent } from '@/shared/utils/getStatusIntent';
import { renderStatus } from '@/shared/utils/renderStatus';
import { IFilterItem } from '@/shared/view/ListLayout/Filters';
import { IColumnConfig } from '@/shared/view/ListLayout/List';
import { ListLayoutView } from '@/shared/view/ListLayout/ListLayout';
import { MobileListItem } from '@/shared/view/ListLayout/MobileListItem';
import { Icon } from '@src-common/view/components/Icon';

import s from './styles.module.css';

type Filters = 'from' | 'to' | 'driverId' | 'number';

type Props = {
  journeys: EntityPage<IJourney>;
  chauffeurs: IDriverBrief[];
  forChauffeur?: string;
  fromDate?: Date;
  toDate?: Date;
  fetchJourneys: (_: any, pagination: { offset: number; limit: number }) => void;
  requestJourneysReport: ({
    driverId,
    from,
    to,
  }: {
    driverId?: string | undefined;
    from?: Date | undefined;
    to?: Date | undefined;
  }) => Promise<any>;
  isLoading?: boolean;
};

const View = ({
  journeys,
  chauffeurs,
  fetchJourneys,
  requestJourneysReport,
  forChauffeur,
  fromDate,
  toDate,
  isLoading = false,
}: Props) => {
  const intl = useIntl();
  const history = useHistory();
  const [isRequestingReport, setIsRequestingReport] = useState(false);
  const [filters, setFilters] = useState<
    Partial<{
      from: Date;
      to: Date;
      driverId: string;
      number: string;
    }>
  >({
    driverId: forChauffeur,
    from: fromDate,
    to: toDate,
  });

  const handleRowClick = useCallback(
    (journey?: IJourney) => {
      if (journey) {
        history.push(`/journeys/${journey.id}`);
      }
    },
    [history],
  );

  const onExcelButtonClick = useCallback(async () => {
    if (isRequestingReport) {
      return;
    }

    try {
      setIsRequestingReport(true);
      await requestJourneysReport({
        driverId: filters.driverId,
        from: filters.from,
        to: filters.to,
      });
    } catch (error) {
      errorHandling.captureException(error);
    } finally {
      setIsRequestingReport(false);
    }
  }, [filters, requestJourneysReport, isRequestingReport]);

  const filterConfig: IFilterItem<Filters>[] = useMemo(
    () => [
      {
        name: 'from',
        type: 'date',
        hasOneLineLabel: true,
        fixedValue: fromDate,
        title: (
          <FormattedMessage
            defaultMessage="From"
            id="Yd9/E1"
            description="Journeys table: From filter"
          />
        ),
      },
      {
        name: 'to',
        type: 'date',
        hasOneLineLabel: true,
        fixedValue: toDate,
        title: (
          <FormattedMessage
            defaultMessage="To"
            id="Uxa2xM"
            description="Journeys table: To filter"
          />
        ),
      },
      {
        name: 'driverId',
        type: 'select',
        placeholder: '',
        hasOneLineLabel: true,
        title: (
          <FormattedMessage
            defaultMessage="Chauffeur"
            id="PFkoxL"
            description="Journeys table: Chauffeurs filter"
          />
        ),
        options: chauffeurs.map(chauffeur => ({
          title: `${chauffeur.firstName} ${chauffeur.lastName}`,
          value: chauffeur.id,
        })),
        fixedValue: forChauffeur,
        isClearable: true,
      },
      {
        name: 'number',
        type: 'text',
        icon: <Icon icon="search" size="18px" className={s.searchIcon} />,
        hasOneLineLabel: true,
        title: (
          <FormattedMessage
            defaultMessage="Search by number"
            id="1k+oza"
            description="Journeys table: Number filter"
          />
        ),
      },
    ],
    [chauffeurs, forChauffeur, fromDate, toDate],
  );

  const columns: IColumnConfig<IJourney>[] = useMemo(
    () => [
      {
        renderHeader: () => '#',
        renderCell: (_, _1, index) => journeys.offset + index + 1,
        style: { width: '40px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Date"
            id="TLkwfC"
            description="Journeys table: Date column header"
          />
        ),
        renderCell: journey =>
          journey.completedAt ? (
            <FormattedDate
              value={journey.completedAt}
              timeZone={journey.timezone}
              year={'numeric'}
              month={'numeric'}
              day={'numeric'}
              hour={'numeric'}
              minute={'numeric'}
              second={'numeric'}
            />
          ) : (
            '–'
          ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Service"
            id="le/TJw"
            description="Journeys table: Service column header"
          />
        ),
        renderCell: journey => journey.service,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Status"
            id="KC13My"
            description="Journeys table: Status column header"
          />
        ),
        renderCell: journey => (
          <Tag intent={getStatusIntent(journey.status)}>{renderStatus(journey.status, intl)}</Tag>
        ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Cost"
            id="y4M8hm"
            description="Journeys table: Cost column header"
          />
        ),
        renderCell: journey =>
          journey.price !== undefined ? (
            <FormattedNumber
              value={journey.price}
              style="currency"
              currency={journey.currencyCode}
            />
          ) : (
            '–'
          ),
        style: { width: '100px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Vehicle"
            id="xHSdWR"
            description="Journeys table: Vehicle column header"
          />
        ),
        renderCell: journey => (journey.vehicle ? journey.vehicle.licensePlate : '–'),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Chauffeur"
            id="PWyHXt"
            description="Journeys table: Chauffeur column header"
          />
        ),
        renderCell: journey =>
          journey.chauffeur ? `${journey.chauffeur.firstName} ${journey.chauffeur.lastName}` : '–',
      },
    ],
    [intl, journeys.offset],
  );

  return (
    <div className={s.root}>
      <ListLayoutView<IJourney, Filters>
        title={
          <FormattedMessage
            defaultMessage="Journeys"
            id="vtkn7V"
            description="Journeys page title"
          />
        }
        data={journeys.items}
        total={journeys.total}
        limit={journeys.limit}
        columns={columns}
        getEntityId={journey => String(journey.id)}
        onFetchData={fetchJourneys}
        onFiltersChange={setFilters}
        onClickRow={handleRowClick}
        shouldBeEmptyWithoutFilters={false}
        filterConfig={filterConfig}
        isLoading={isLoading}
        after={
          <button
            disabled={isRequestingReport || journeys.items.length === 0}
            className={s.excelButton}
            onClick={onExcelButtonClick}
          >
            <FormattedMessage
              defaultMessage="Send Excel to email"
              id="qY30KY"
              description="Send Excel button label"
            />{' '}
            <Icon icon="arrow-to-end-in-circle" size="24px" />
          </button>
        }
        renderItem={journey => (
          <MobileListItem
            title={
              <div className={s.title}>
                {journey.vehicle ? journey.vehicle.licensePlate : '–'}
                <Tag className={s.tag} intent={getStatusIntent(journey.status)}>
                  {renderStatus(journey.status, intl)}
                </Tag>
              </div>
            }
            note={
              journey.completedAt ? (
                <FormattedDate
                  value={journey.completedAt}
                  timeZone={journey.timezone}
                  year={'numeric'}
                  month={'numeric'}
                  day={'numeric'}
                  hour={'numeric'}
                  minute={'numeric'}
                  second={'numeric'}
                />
              ) : (
                '–'
              )
            }
            right={
              journey.price ? (
                <FormattedNumber
                  value={journey.price}
                  style="currency"
                  currency={journey.currencyCode}
                />
              ) : (
                '–'
              )
            }
          />
        )}
      />
    </div>
  );
};

export { View };
